import React, { useState, useEffect } from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination } from "react-table";
import { FaEnvelopeOpenText, FaFileSignature, FaTasks, FaEllipsisH, FaCloudUploadAlt, FaFileAlt, FaCloudDownloadAlt, FaSyncAlt, FaUser, FaRegBuilding, FaUserEdit, FaMapMarkerAlt, FaEye, FaEdit, FaTrashAlt, FaPlus } from "react-icons/fa";
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Dropdown from 'react-bootstrap/Dropdown';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Tooltip, Button } from 'react-bootstrap';

// Custom filter function for column-specific filtering
function filterRows(rows, columnFilters) {
    return rows.filter(row => {
        let isMatch = true;
        for (const [columnId, filterValue] of Object.entries(columnFilters)) {
            if (filterValue && filterValue.trim() !== '') {
                const cellValue = row.values[columnId];
                if (cellValue !== undefined && cellValue !== null) {
                    const stringValue = String(cellValue).toLowerCase();
                    const searchTerm = filterValue.toLowerCase().trim();
                    if (!stringValue.includes(searchTerm)) {
                        isMatch = false;
                        break;
                    }
                } else {
                    isMatch = false;
                    break;
                }
            }
        }
        return isMatch;
    });
}

function GlobalFilter({ globalFilter, setGlobalFilter }) {
    return (
        <span className="d-flex ms-auto table-filterSearch">
            <input
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="form-control mb-4"
                placeholder="Global Search..."
            />
        </span>
    );
}

// Column Filter component for individual column search
function ColumnFilter({ column, onFilterChange }) {
    const [value, setValue] = useState('');

    const handleChange = (e) => {
        const newValue = e.target.value;
        setValue(newValue);
        onFilterChange(column.id, newValue);
    };

    return (
        <input
            type="text"
            value={value}
            onChange={handleChange}
            className="form-control form-control-sm"
            placeholder={`Search ${column.Header || ''}...`}
            style={{ width: '100%', minWidth: '80px' }}
            onClick={(e) => e.stopPropagation()}
        />
    );
}

const Tables = ({
    columns,
    data,
    actions,
    conditionalActions,
    subActions,
    onViewDoc,
    onDocumnetShared,
    onValidate,
    onReMapping,
    onReupload,
    onUpload,
    onLog,
    onEdit,
    onView,
    onDownload,
    onSwitchToggle,
    onDelete,
    onAddressInfo,
    onGstInfo,
    onBankInfo,
    onContactInfo,
    onQuotation,
    onPayout,
    onChk,
    onToggleClick,
    onToggleClickEL,
    onToggleClickNDA,
    onSendQuotations,
    onGenerateQuotation,
    onAdd,
    onActivityEmail,
    onIsActive,
    onIsDefault,
    onGenrateInvoice,
    onCreateProject,
    onPay,
    onSendEmail,
    onSendEmails,
    onUploadDocument,
    onAddUpdateStatus,
    onPdfDownload,
    onExcelDownload,
    onDataQueryDownload,
    onReUpload,
    onReValidate,
    onViewHistory,
    onReAssumption
}) => {
    const [showBtn, setShowBtn] = useState(false);
    const [showBtns, setShowBtns] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedProId, setSelectedProId] = useState([]);

    // State for column filters
    const [columnFilters, setColumnFilters] = useState({});

    // Apply column filters to data
    const filteredData = React.useMemo(() => {
        if (Object.keys(columnFilters).length === 0) {
            return data;
        }
        return filterRows(data.map(row => ({ ...row, values: row })), columnFilters);
    }, [data, columnFilters]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        state,
        setGlobalFilter,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        pageOptions,
        gotoPage,
        pageCount,
        setPageSize,
    } = useTable(
        {
            columns,
            data: filteredData,
            initialState: { pageIndex: 0, pageSize: 20 },
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const { globalFilter, pageIndex, pageSize } = state;

    // Handle column filter change
    const handleColumnFilterChange = (columnId, value) => {
        setColumnFilters(prev => ({
            ...prev,
            [columnId]: value
        }));
    };

    const handleProCheckboxChange = (rowData, isChecked) => {
        let updatedSelectedIds;

        if (isChecked) {
            updatedSelectedIds = [...selectedProId, rowData.id];
        } else {
            updatedSelectedIds = selectedProId.filter(id => id !== rowData.id);
        }
        setSelectedProId(updatedSelectedIds);
        setShowBtns(updatedSelectedIds.length > 0);

        if (onChk && typeof onChk === 'function') {
            onChk(updatedSelectedIds);
        }
    };

    const handleCheckboxChange = (rowData, isChecked) => {
        let updatedSelectedIds;

        if (isChecked) {
            updatedSelectedIds = [...selectedIds, rowData.id];
        } else {
            updatedSelectedIds = selectedIds.filter(id => id !== rowData.id);
        }

        setSelectedIds(updatedSelectedIds);
        setShowBtn(updatedSelectedIds.length > 0);

        if (onChk && typeof onChk === 'function') {
            onChk(updatedSelectedIds);
        }
    };

    const isRowSelected = (rowId) => {
        return selectedIds.includes(rowId);
    };

    const isRowProSelected = (rowId) => {
        return selectedProId.includes(rowId);
    };

    const getActionsForRow = (rowData) => {
        if (conditionalActions && typeof conditionalActions == 'function') {
            const conditionalResult = conditionalActions(rowData);
            return Array.isArray(conditionalResult) ? conditionalResult : (actions || []);
        }
        return actions || [];
    };

    // Determine which columns should have filter (exclude action columns, checkbox columns, etc.)
    const getFilterableColumns = () => {
        const excludeColumns = ['action', 'bAction', 'pAction', 'switch', 'isactive', 'isdefault', 'designation_count', 'sub_sector_count', 'data_count', 'sub_category_count', 'is_data_validate', 'com_type_icon'];
        return columns.filter(col => !excludeColumns.includes(col.id) && col.id !== '');
    };

    return (
        <div>
            <div className="d-flex">
                {showBtn &&
                    <div style={{ marginLeft: 150 }}>
                        <div className='d-flex'>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    onToggleClick && onToggleClick(selectedIds);
                                }}
                            >
                                Assigned To ({selectedIds.length})
                            </Button>
                        </div>
                    </div>
                }

                {showBtns &&
                    <div style={{ marginLeft: 136 }}>
                        <div className='d-flex'>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    onToggleClickEL && onToggleClickEL(selectedProId);
                                }}
                            >
                                Generate & Send Engagement Letter
                            </Button>

                            <Button
                                style={{ marginLeft: 15 }}
                                variant="primary"
                                onClick={() => {
                                    onToggleClickNDA && onToggleClickNDA(selectedProId);
                                }}
                            >
                                Generate & Send NDA Form
                            </Button>
                        </div>
                    </div>
                }
                <GlobalFilter
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                />
            </div>
            <div className="table-responsive">
                <table
                    {...getTableProps()}
                    className="table table-bordered text-nowrap mb-0"
                >
                    <thead>
                        {headerGroups?.map((headerGroup) => {
                            const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                            return (
                                <tr
                                    key={headerGroupKey}
                                    className="text-center"
                                    {...headerGroupProps}
                                >
                                    {headerGroup?.headers.map((column) => {
                                        const { key: columnKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps());
                                        return (
                                            <th
                                                key={columnKey}
                                                {...columnProps}
                                                className={column.className}
                                                style={{ width: column?.width }}
                                            >
                                                <span className="tabletitle">{column.render("Header")}</span>
                                                <span>
                                                    {column.isSorted ? (
                                                        column.isSortedDesc ? (
                                                            <i className="fa fa-angle-down"></i>
                                                        ) : (
                                                            <i className="fa fa-angle-up"></i>
                                                        )
                                                    ) : (
                                                        ""
                                                    )}
                                                </span>
                                            </th>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        {/* Filter Row - Second row for column-specific search */}
                        <tr className="filter-row">
                            {headerGroups?.map((headerGroup) => {
                                const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                                return (
                                    <React.Fragment key={`filter-${headerGroupKey}`}>
                                        {headerGroup?.headers.map((column) => {
                                            const { key: columnKey, ...columnProps } = column.getHeaderProps();
                                            const excludeColumns = ['action', 'bAction', 'pAction', 'switch', 'isactive', 'isdefault', 'designation_count', 'sub_sector_count', 'data_count', 'sub_category_count', 'is_data_validate', 'com_type_icon'];
                                            const isFilterable = !excludeColumns.includes(column.id) && column.id !== '';

                                            return (
                                                <th
                                                    key={`filter-${columnKey}`}
                                                    {...columnProps}
                                                    className={column.className}
                                                    style={{ width: column?.width, padding: '5px' }}
                                                >
                                                    {isFilterable && (
                                                        <ColumnFilter
                                                            column={column}
                                                            onFilterChange={handleColumnFilterChange}
                                                        />
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody {...getTableBodyProps()}>
                        {page?.length > 0 ? (
                            page?.map((row) => {
                                prepareRow(row);
                                const rowData = row.original;
                                const { key: rowKey, ...rowProps } = row.getRowProps();

                                return (
                                    <tr
                                        key={rowKey}
                                        className="text-center"
                                        {...rowProps}
                                    >
                                        {row?.cells?.map((cell) => {
                                            const { key: cellKey, ...cellProps } = cell.getCellProps();
                                            const stageValue = cell.value;

                                            if (cell.column.id === 'stageName') {
                                                return (
                                                    <td key={cellKey}>
                                                        <span className={`stageBox ${stageValue}`}>{cell.render("Cell")}</span>
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'pAction') {
                                                const isChecked = isRowProSelected(rowData.id);

                                                return (
                                                    <td key={cellKey}>
                                                        <span className='chk'>
                                                            {rowData.assigned && rowData.assigned.trim() !== '' ? (
                                                                <input type='checkbox' className='chkbox disabled' disabled checked={true} />
                                                            ) : (
                                                                <input
                                                                    type='checkbox'
                                                                    className='chkbox'
                                                                    checked={isChecked}
                                                                    onChange={(e) => {
                                                                        handleProCheckboxChange(rowData, e.target.checked);
                                                                    }}
                                                                />
                                                            )}
                                                        </span>
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'bAction') {
                                                const isChecked = isRowSelected(rowData.id);

                                                return (
                                                    <td key={cellKey}>
                                                        <span className='chk'>
                                                            {rowData.assigned && rowData.assigned.trim() !== '' ? (
                                                                <input type='checkbox' className='chkbox disabled' disabled checked={true} />
                                                            ) : (
                                                                <input
                                                                    type='checkbox'
                                                                    className='chkbox'
                                                                    checked={isChecked}
                                                                    onChange={(e) => {
                                                                        handleCheckboxChange(rowData, e.target.checked);
                                                                    }}
                                                                />
                                                            )}
                                                        </span>
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'action') {
                                                const rowActions = getActionsForRow(rowData);

                                                return (
                                                    <td key={cellKey}>
                                                        <div className="d-flex justify-content-center gap-2">
                                                            {rowActions?.map((items, ind) => {
                                                                return (
                                                                    <div key={`actions_${ind}`}>
                                                                        {items == 'switch' &&
                                                                            <div className="switchs">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={rowData.isActive == 1 ? true : false}
                                                                                    onChange={() => onSwitchToggle && onSwitchToggle(rowData.id)}
                                                                                />
                                                                                <span className="slider round"></span>
                                                                            </div>
                                                                        }

                                                                        {items == 'isActive' &&
                                                                            <div className="switchs">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={rowData.is_active ? true : false}
                                                                                    //onChange={() => onIsActive && onIsActive(rowData)}
                                                                                    disabled
                                                                                />
                                                                                <span className="slider round"></span>
                                                                            </div>
                                                                        }

                                                                        {items == 'userswitch' &&
                                                                            <div className="switchs">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={rowData.is_user_profile ? true : false}
                                                                                    onChange={() => onSwitchToggle && onSwitchToggle(rowData)}
                                                                                />
                                                                                <span className="slider round"></span>
                                                                            </div>
                                                                        }

                                                                        {items == 'Add' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Add </Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onAdd && onAdd(rowData)}>
                                                                                    <FaPlus />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'genrate' &&
                                                                            <button className='btn btn-primary' onClick={() => onGenrateInvoice && onGenrateInvoice(rowData)}>Genrate Invoice</button>
                                                                        }

                                                                        {items == 'sendEmail' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Send Email</Tooltip>}                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onActivityEmail && onActivityEmail(rowData)}>
                                                                                    <FaEnvelopeOpenText />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'edit' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Edit</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onEdit && onEdit(rowData)}>
                                                                                    <FaEdit />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'View Activity' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>View Activity</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onView && onView(rowData)}>
                                                                                    <FaEye />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }


                                                                        {items == 'View Document' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>View Document</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onView && onView(rowData)}>
                                                                                    <FaEye />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'reupload' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Re Upload</Tooltip>}
                                                                            >
                                                                                <button className='btn-action reupload' onClick={() => onReupload && onReupload(rowData)}>
                                                                                    <FaCloudUploadAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'remapping' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Re Mapping</Tooltip>}
                                                                            >
                                                                                <button className='btn-action mapping' onClick={() => onReMapping && onReMapping(rowData)}>
                                                                                    <FaTasks />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'validate' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Validate</Tooltip>}
                                                                            >
                                                                                <button className='btn-action reupload' onClick={() => onValidate && onValidate(rowData)}>
                                                                                    <i className='fa fa-list'></i>
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }


                                                                        {items == 'Generate Quotation' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Generate Quotation</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onGenerateQuotation && onGenerateQuotation(rowData)}>
                                                                                    <FaFileSignature />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Send Quotations' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Send Quotations</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onSendQuotations && onSendQuotations(rowData)}>
                                                                                    <FaEnvelopeOpenText />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'log' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Communication Log</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onLog && onLog(rowData)}>
                                                                                    <FaSyncAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Send Quotation' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip> Send Quotation</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onQuotation && onQuotation(rowData)}>
                                                                                    <FaFileAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Documnet Shared' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip> Documnet Shared</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onDocumnetShared && onDocumnetShared(rowData)}>
                                                                                    <FaCloudUploadAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }


                                                                        {items == 'delete' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Delete</Tooltip>}
                                                                            >
                                                                                <button className='btn-action delete' onClick={() => onDelete && onDelete(rowData)}>
                                                                                    <FaTrashAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Invoice Download' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Invoice Download</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onPdfDownload && onPdfDownload(rowData)}>
                                                                                    <FaCloudDownloadAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Pdf Download' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Pdf Download</Tooltip>}
                                                                            >
                                                                                <button className='btn-action reupload' onClick={() => onPdfDownload && onPdfDownload(rowData)}>
                                                                                    <FaCloudDownloadAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Excel Download' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Excel Download</Tooltip>}
                                                                            >
                                                                                <button className='btn-action reupload' onClick={() => onExcelDownload && onExcelDownload(rowData)}>
                                                                                    <FaCloudDownloadAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Data Query Download' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Data Query Download</Tooltip>}
                                                                            >
                                                                                <button className='btn-action reupload' onClick={() => onDataQueryDownload && onDataQueryDownload(rowData)}>
                                                                                    <FaCloudDownloadAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Re Upload' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Re upload</Tooltip>}
                                                                            >
                                                                                <button className='btn-action reupload' onClick={() => onReUpload && onReUpload(rowData)}>
                                                                                    <FaCloudUploadAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Re Validate' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Re Validate</Tooltip>}
                                                                            >
                                                                                <button className='btn-action reupload' onClick={() => onReValidate && onReValidate(rowData)}>
                                                                                    <FaCloudUploadAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'View History' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>View History</Tooltip>}
                                                                            >
                                                                                <button className='btn-action reupload' onClick={() => onViewHistory && onViewHistory(rowData)}>
                                                                                    <i className='fa fa-info'></i>
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Re Assumption' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Re Assumption</Tooltip>}
                                                                            >
                                                                                <button className='btn-action reupload' onClick={() => onReAssumption && onReAssumption(rowData)}>
                                                                                    <i className="fe fe-layers"></i>
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Upload Document' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Upload Document</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onUploadDocument && onUploadDocument(rowData)}>
                                                                                    <FaCloudUploadAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'sendEmails' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Send Email</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onSendEmails && onSendEmails(rowData)}>
                                                                                    <FaEnvelopeOpenText />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Address' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Address</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onAddressInfo && onAddressInfo(rowData, 'address')}>
                                                                                    <FaMapMarkerAlt />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'gstInfo' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Gst Info</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onGstInfo && onGstInfo(rowData, 'gst')}>
                                                                                    <i className="mdi mdi-account-card-details side-menu__icon"></i>
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'bankInfo' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Bank Info</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onBankInfo && onBankInfo(rowData, 'bank')}>
                                                                                    <FaRegBuilding />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'contactInfo' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Contact Info</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onContactInfo && onContactInfo(rowData)}>
                                                                                    <FaUserEdit />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'Payout Commission' &&
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<Tooltip>Payout Commission</Tooltip>}
                                                                            >
                                                                                <button className='btn-action edit' onClick={() => onPayout && onPayout(rowData)}>
                                                                                    <FaUser />
                                                                                </button>
                                                                            </OverlayTrigger>
                                                                        }

                                                                        {items == 'createProject' &&
                                                                            <button className='btn btn-primary' onClick={() => onCreateProject && onCreateProject(rowData)}>Leads Create Project</button>
                                                                        }

                                                                        {items == 'pay' &&
                                                                            <button className='btn btn-primary' onClick={() => onPay && onPay(rowData)}>Pay</button>
                                                                        }

                                                                        {items == 'add Status' &&
                                                                            <button className='btn btn-primary' onClick={() => onAddUpdateStatus && onAddUpdateStatus(rowData)}>Add/Update Status</button>
                                                                        }
                                                                    </div>
                                                                );
                                                            })}

                                                            {subActions?.length > 0 && (
                                                                <Dropdown as={ButtonGroup}>
                                                                    <Dropdown.Toggle><FaEllipsisH /></Dropdown.Toggle>
                                                                    <Dropdown.Menu>
                                                                        {subActions?.map((items, index) => {
                                                                            return (
                                                                                <div key={`subActions_${index}`}>
                                                                                    {items == 'user' &&
                                                                                        <Dropdown.Item onClick={() => onLog && onLog(rowData)}>
                                                                                            <div className='icons'><FaUser /></div>
                                                                                            user
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'Payout Commission' &&
                                                                                        <Dropdown.Item onClick={() => onPayout && onPayout(rowData)}>
                                                                                            <div className='icons'><FaUser /></div>
                                                                                            Payout Commission
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'Address' &&
                                                                                        <Dropdown.Item onClick={() => onAddressInfo && onAddressInfo(rowData, 'address')}>
                                                                                            <div className='icons'><FaMapMarkerAlt /></div>
                                                                                            Address
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'gstInfo' &&
                                                                                        <Dropdown.Item onClick={() => onGstInfo && onGstInfo(rowData, 'gst')}>
                                                                                            <div className='icons'><i className="mdi mdi-account-card-details side-menu__icon"></i></div>
                                                                                            Gst Info
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'bankInfo' &&
                                                                                        <Dropdown.Item onClick={() => onBankInfo && onBankInfo(rowData, 'bank')}>
                                                                                            <div className='icons'><FaRegBuilding /></div>
                                                                                            Bank Info
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'contactInfo' &&
                                                                                        <Dropdown.Item onClick={() => onContactInfo && onContactInfo(rowData, 'contact')}>
                                                                                            <div className='icons'><FaUserEdit /></div>
                                                                                            Contact Info
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'Send Quotation' &&
                                                                                        <Dropdown.Item onClick={() => onQuotation && onQuotation(rowData)}>
                                                                                            <div className='icons'><FaFileAlt /></div>
                                                                                            Send Quotation
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'log' &&
                                                                                        <Dropdown.Item onClick={() => onLog && onLog(rowData)}>
                                                                                            <div className='icons'><FaSyncAlt /></div>
                                                                                            Communication Log
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'view' &&
                                                                                        <Dropdown.Item onClick={() => onView && onView(rowData)}>
                                                                                            <div className='icons'><FaEye /></div>
                                                                                            View
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'view-Document' &&
                                                                                        <Dropdown.Item onClick={() => onViewDoc && onViewDoc(rowData)}>
                                                                                            <div className='icons'><FaEye /></div>
                                                                                            View Document
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'download' &&
                                                                                        <Dropdown.Item onClick={() => onDownload && onDownload(rowData)}>
                                                                                            <div className='icons'><FaCloudDownloadAlt /></div>
                                                                                            Download
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'sendEmail' &&
                                                                                        <Dropdown.Item onClick={() => onSendEmail && onSendEmail(rowData)}>
                                                                                            <div className='icons'><FaEnvelopeOpenText /></div>
                                                                                            Send Email
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'uploads' &&
                                                                                        <Dropdown.Item onClick={() => onUpload && onUpload(rowData)}>
                                                                                            <div className='icons'><FaCloudUploadAlt /></div>
                                                                                            Upload Sample Report
                                                                                        </Dropdown.Item>
                                                                                    }

                                                                                    {items == 'Documnet Shared' &&
                                                                                        <Dropdown.Item onClick={() => onDocumnetShared && onDocumnetShared(rowData)}>
                                                                                            <div className='icons'><FaCloudUploadAlt /></div>
                                                                                            Documnet Shared
                                                                                        </Dropdown.Item>
                                                                                    }
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'switch') {
                                                return (
                                                    <td key={cellKey}>
                                                        <div className="switchs">
                                                            <input type="checkbox"
                                                                onClick={() => {
                                                                    setIsActive(!isActive)
                                                                }} />
                                                            <span className="slider round"></span>
                                                        </div>
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'designation_count') {
                                                return (
                                                    <td key={cellKey}>
                                                        <Link to={'/masters/designation/' + rowData?.position_id}>{cell.render("Cell")}</Link>
                                                    </td>
                                                );
                                            }
                                            if (cell.column.id === 'com_type_icon') {
                                                return (
                                                    <td key={cellKey}>
                                                        <div className='iconsimg'>
                                                            <img src={rowData?.com_type_icon} />
                                                        </div>
                                                    </td>
                                                );
                                            }
                                            if (cell.column.id === 'sub_sector_count') {
                                                return (
                                                    <td key={cellKey}>
                                                        <Link to={'/masters/sectors/' + rowData?.sector_id}>{cell.render("Cell")}</Link>
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'data_count') {
                                                return (
                                                    <td key={cellKey}>
                                                        <Link to={'/master/subMaster/' + rowData?.id}>{cell.render("Cell")}</Link>
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'sub_category_count') {
                                                return (
                                                    <td key={cellKey}>
                                                        <Link to={'/masters/product-category/' + rowData?.id}>{cell.render("Cell")}</Link>
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'is_data_validate') {
                                                return (
                                                    <td key={cellKey}>
                                                        {stageValue ? 1 : 0}
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'isactive') {
                                                return (
                                                    <td style={{ width: cell?.column?.width }} key={cellKey}>
                                                        <div className="switchs">
                                                            <input
                                                                type="checkbox"
                                                                style={{ cursor: 'no-drop' }}
                                                                checked={rowData.is_active ? true : false}
                                                                //onChange={() => onIsActive && onIsActive(rowData)}
                                                                disabled
                                                            />
                                                            <span className="slider round"></span>
                                                        </div>
                                                    </td>
                                                );
                                            }

                                            if (cell.column.id === 'isdefault') {
                                                return (
                                                    <td style={{ width: cell?.column?.width }} key={cellKey}>
                                                        <div className="switchs">
                                                            <input
                                                                type="checkbox"
                                                                style={{ cursor: 'no-drop' }}
                                                                checked={rowData.is_default ? true : false}
                                                                //onChange={() => onIsDefault && onIsDefault(rowData)}
                                                                disabled
                                                            />
                                                            <span className="slider round"></span>
                                                        </div>
                                                    </td>
                                                );
                                            }

                                            return (
                                                <td
                                                    key={cellKey}
                                                    {...cellProps}
                                                >
                                                    {cell.render("Cell")}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className="text-center py-5"
                                >
                                    <div className="no-data-message">
                                        <i className="fa fa-database fa-3x mb-3 text-muted"></i>
                                        <h5 className="text-muted">No Data Available</h5>
                                        <p className="text-muted mb-0">There are no records to display</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Only show pagination if there is data */}
            {data.length > 0 && (
                <div className="d-block d-sm-flex mt-4 align-items-center">
                    <div>
                        <div className="d-block d-sm-flex align-items-center">
                            <select
                                className="mr-4 selectpage border"
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                            >
                                {[20, 50, 100, 200, 500]?.map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        Show {pageSize}
                                    </option>
                                ))}
                            </select>
                            <span className="">
                                Page{" "}
                                <strong>
                                    {pageIndex + 1} of {pageOptions.length}
                                </strong>{" "}
                            </span>
                        </div>
                    </div>

                    <span className="ms-sm-auto">
                        <Button
                            variant=""
                            className="btn-default tablebutton me-2 d-sm-inline d-block my-1"
                            onClick={() => gotoPage(0)}
                            disabled={!canPreviousPage}
                        >
                            {" Previous "}
                        </Button>
                        <Button
                            variant=""
                            className="btn-default tablebutton me-2 my-1"
                            onClick={() => previousPage()}
                            disabled={!canPreviousPage}
                        >
                            {" << "}
                        </Button>
                        <Button
                            variant=""
                            className="btn-default tablebutton me-2 my-1"
                            onClick={() => previousPage()}
                            disabled={!canPreviousPage}
                        >
                            {" < "}
                        </Button>
                        <Button
                            variant=""
                            className="btn-default tablebutton me-2 my-1"
                            onClick={() => nextPage()}
                            disabled={!canNextPage}
                        >
                            {" > "}
                        </Button>
                        <Button
                            variant=""
                            className="btn-default tablebutton me-2 my-1"
                            onClick={() => nextPage()}
                            disabled={!canNextPage}
                        >
                            {" >> "}
                        </Button>
                        <Button
                            variant=""
                            className="btn-default tablebutton me-2 d-sm-inline d-block my-1"
                            onClick={() => gotoPage(pageCount - 1)}
                            disabled={!canNextPage}
                        >
                            {" Next "}
                        </Button>
                    </span>
                </div>
            )}
        </div>
    );
}

export default Tables;